import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import copy from 'copy-to-clipboard';
import { format } from 'date-fns';

import checkIcon from 'core/assets/svg/check-green.svg';
import { Button } from 'core/components/button';
import { NotificationCard } from 'core/components/notification-card';
import { useToast } from 'core/components/toast';
import { phoneNumberToStringFormatter } from 'core/helpers/formatters';
import { useStore } from 'core/store';
import emailIcon from '../assets/email-icon-orange.svg';
import smsIcon from '../assets/sms-icon-orange.svg';
import copyIcon from '../assets/copy-squares-icon.svg';
import { Method } from '../helpers';
import useStyles from './purchase-link-sent.styles';

const PurchaseLinkSent = ({ loading, onSendLink, lastSentMethod, passesActivePolicyCheck }) => {
  const classes = useStyles();
  const {
    offer: { offer }
  } = useStore();
  const { purchaseLinkSentTimestamp, purchaseLinkSentBy, purchaseLink } = offer;
  const toast = useToast();
  const { values, dirty } = useFormikContext();
  // The clean-form copy path awaits the active-policy check without going through onSendLink, so it
  // holds its own flag to keep these buttons from firing a second check.
  const [copying, setCopying] = useState(false);

  const copyLink = async () => {
    setCopying(true);
    try {
      // if the form is dirty, we need to update the offer
      if (dirty) {
        // this check is just in case they reload the page
        if (!lastSentMethod) {
          toast.notify({
            type: 'error',
            message:
              'There was an error copying the purchase link due to changes in the form, please try sending the link again.'
          });
          return;
        }
        // A re-send that never happened leaves `purchaseLink` pointing at the stale offer, and a
        // re-send blocked by the active-policy match must not be handed over anyway (#34591).
        if (!(await onSendLink(lastSentMethod))) {
          return;
        }
      } else if (!(await passesActivePolicyCheck())) {
        // Copying is a hand-off too: a clean form skips the re-send, so it needs the gate directly.
        return;
      }
      const copied = copy(purchaseLink);
      if (copied) {
        toast.notify({
          type: 'success',
          message: 'The link is copied to your clipboard. This link is for customer use ONLY.'
        });
      } else {
        toast.notify({
          type: 'error',
          message: `We could not copy the link, but here it is: ${purchaseLink}`
        });
      }
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className={classes.container}>
      <NotificationCard icon={checkIcon} className={classes.notification} type="light">
        Link sent on {format(new Date(purchaseLinkSentTimestamp), 'MM/dd/yyyy @ h:mm a')}. An email to{' '}
        {purchaseLinkSentBy} will be sent once the purchase is complete.
      </NotificationCard>
      <div className={classes.links}>
        {values.email && (
          <Button disabled={loading} variant="text" color="secondary" onClick={() => onSendLink(Method.Email)}>
            <span className={classes.link}>
              <img alt="Outlined orange envelope" src={emailIcon} />
              Re-send link to {values.email}
            </span>
          </Button>
        )}
        {values.phone && (
          <Button disabled={loading} variant="text" color="secondary" onClick={() => onSendLink(Method.Sms)}>
            <span className={classes.link}>
              <img alt="Outlined orange cellphone and speech balloon" src={smsIcon} />
              Re-send link to {phoneNumberToStringFormatter(values.phone)}
            </span>
          </Button>
        )}
        <Button disabled={loading || copying} variant="text" color="secondary" onClick={copyLink}>
          <span className={classes.link}>
            <img alt="Two overlapped squares" src={copyIcon} />
            Copy purchase link to clipboard
          </span>
        </Button>
      </div>
    </div>
  );
};

PurchaseLinkSent.propTypes = {
  loading: PropTypes.bool.isRequired,
  onSendLink: PropTypes.func.isRequired,
  lastSentMethod: PropTypes.string,
  passesActivePolicyCheck: PropTypes.func.isRequired
};

export default observer(PurchaseLinkSent);
